import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { AppFooter } from '../components/AppFooter';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/Accordion';
import { Button } from '../components/Button';
export function Faq() {
  const navigate = useNavigate();
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [{
      '@type': 'Question',
      name: 'Is it safe to get medical treatment abroad?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Medical travel can be highly secure when executed with rigorous planning and due diligence. We coordinate care exclusively with facilities that maintain recognized international accreditations. These institutions adhere to stringent clinical protocols and safety standards that are designed to mirror or exceed those found in major domestic healthcare systems.'
      }
    }, {
      '@type': 'Question',
      name: 'How are the hospitals and doctors vetted?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Our vetting methodology is comprehensive. We evaluate facilities based on their adherence to international accreditation standards (such as those modeled by organizations like the Joint Commission International, cited here illustratively). We also review the credentials of the medical staff, prioritizing surgeons who hold board certifications or have completed fellowships in recognized medical hubs. Facility infrastructure, including intensive care capabilities, is also a critical factor in our assessment.'
      }
    }, {
      '@type': 'Question',
      name: 'What happens if I need follow-up care when I return home?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Continuity of care is a fundamental component of the medical travel process. Before you depart your destination, the international medical team will provide you with comprehensive medical records, operative notes, and imaging, translated into English. We strongly advise patients to establish a relationship with a local primary care physician or specialist prior to travel, ensuring a seamless transition for post-operative monitoring upon your return.'
      }
    }, {
      '@type': 'Question',
      name: 'Does my health insurance cover medical tourism?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Traditional domestic health insurance policies typically do not cover elective procedures performed internationally. However, the out-of-pocket costs for medical travel are often substantially lower than the deductibles, co-pays, or uninsured costs for the equivalent procedure domestically. We recommend consulting directly with your insurance provider regarding any out-of-network or international coverage benefits you may possess.'
      }
    }, {
      '@type': 'Question',
      name: 'Do I need to arrange my own travel?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You maintain the flexibility to manage your own travel logistics if preferred. However, our care coordination service includes comprehensive logistical support. We can assist with scheduling flights, arranging secure airport transfers, and booking appropriate accommodations near the medical facility for you and any traveling companions, ensuring a streamlined experience.'
      }
    }, {
      '@type': 'Question',
      name: 'How is pricing transparency maintained?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We prioritize absolute financial clarity. Before you commit to any travel, you will receive a detailed proposal outlining the estimated costs of the medical procedure, facility fees, and anesthesia. While medical treatments can occasionally require unforeseen interventions, the initial proposals are designed to be as comprehensive and transparent as possible, eliminating hidden administrative fees.'
      }
    }, {
      '@type': 'Question',
      name: 'How is the privacy of my medical data protected?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We treat your sensitive health information with the highest level of security. All medical records and diagnostic imaging are transmitted using secure, encrypted channels. We adhere to strict data privacy protocols designed to align with international standards for health information portability and accountability. Your data is shared only with the specific medical teams evaluating your case.'
      }
    }]
  };
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-653">
      <Helmet data-id="element-654">
        <title data-id="element-655">Frequently Asked Questions | Affordable Medical Tourism</title>
        <meta name="description" content="Find answers to common questions about medical tourism, including safety, hospital vetting, travel logistics, and pricing transparency." data-id="element-656" />
        <link rel="canonical" href="https://affordablemedicaltourism.com/faq" data-id="element-657" />
        <script type="application/ld+json" data-id="element-658">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <AppHeader data-id="element-659" />

      <main className="flex-1 py-20 px-6 md:px-12 max-w-4xl mx-auto w-full" data-id="element-660">
        <div className="text-center mb-16" data-id="element-661">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-id="element-662">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-663">
            Comprehensive information regarding the logistics, safety, and
            coordination of international medical care.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-10" data-id="element-664">
          <Accordion type="single" collapsible className="w-full" data-id="element-665">
            <AccordionItem value="item-1" data-id="element-666">
              <AccordionTrigger className="text-left text-lg font-semibold" data-id="element-667">
                Is it safe to get medical treatment abroad?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pt-2 pb-6" data-id="element-668">
                Medical travel can be highly secure when executed with rigorous
                planning and due diligence. We coordinate care exclusively with
                facilities that maintain recognized international
                accreditations. These institutions adhere to stringent clinical
                protocols and safety standards that are designed to mirror or
                exceed those found in major domestic healthcare systems.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2" data-id="element-669">
              <AccordionTrigger className="text-left text-lg font-semibold" data-id="element-670">
                How are the hospitals and doctors vetted?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pt-2 pb-6" data-id="element-671">
                Our vetting methodology is comprehensive. We evaluate facilities
                based on their adherence to international accreditation
                standards, such as those modeled by organizations like the Joint
                Commission International (cited here illustratively). We also
                review the credentials of the medical staff, prioritizing
                surgeons who hold board certifications or have completed
                fellowships in recognized medical hubs. Facility infrastructure,
                including intensive care capabilities, is also a critical factor
                in our assessment.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3" data-id="element-672">
              <AccordionTrigger className="text-left text-lg font-semibold" data-id="element-673">
                What happens if I need follow-up care when I return home?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pt-2 pb-6" data-id="element-674">
                Continuity of care is a fundamental component of the medical
                travel process. Before you depart your destination, the
                international medical team will provide you with comprehensive
                medical records, operative notes, and imaging, translated into
                English. We strongly advise patients to establish a relationship
                with a local primary care physician or specialist prior to
                travel, ensuring a seamless transition for post-operative
                monitoring upon your return.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4" data-id="element-675">
              <AccordionTrigger className="text-left text-lg font-semibold" data-id="element-676">
                Does my health insurance cover medical tourism?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pt-2 pb-6" data-id="element-677">
                Traditional domestic health insurance policies typically do not
                cover elective procedures performed internationally. However,
                the out-of-pocket costs for medical travel are often
                substantially lower than the deductibles, co-pays, or uninsured
                costs for the equivalent procedure domestically. Furthermore, an
                increasing number of self-funded employer health plans are
                actively incorporating medical tourism benefits to reduce
                corporate healthcare expenditures. We assist patients in
                providing the necessary documentation and transparent pricing
                required to utilize these employer-sponsored benefits.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5" data-id="element-678">
              <AccordionTrigger className="text-left text-lg font-semibold" data-id="element-679">
                Do I need to arrange my own travel and logistics?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pt-2 pb-6" data-id="element-680">
                You maintain the flexibility to manage your own travel logistics
                if preferred. However, our care coordination service includes
                comprehensive logistical support. We partner with trusted local
                care facilitators in your destination country. These
                on-the-ground experts assist with scheduling flights, arranging
                secure airport transfers, providing translation services, and
                booking appropriate accommodations (including specialized
                recovery retreats), ensuring a streamlined and stress-free
                experience.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-8" data-id="element-681">
              <AccordionTrigger className="text-left text-lg font-semibold" data-id="element-682">
                What are Medical Tourism Settlement Cities?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pt-2 pb-6" data-id="element-683">
                Medical Tourism Settlement Cities are specific global
                destinations that offer an ideal environment for long-term
                medical care, extensive rehabilitation, or health-focused
                retirement. These cities combine immediate access to elite,
                accredited hospitals with a low cost of living, established
                expat communities, robust assisted-living infrastructure, and
                favorable long-stay visa pathways. We help patients identify the
                perfect city for their long-term health and lifestyle needs.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-6" data-id="element-684">
              <AccordionTrigger className="text-left text-lg font-semibold" data-id="element-685">
                How is pricing transparency maintained?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pt-2 pb-6" data-id="element-686">
                We prioritize absolute financial clarity. Before you commit to
                any travel, you will receive a detailed proposal outlining the
                estimated costs of the medical procedure, facility fees, and
                anesthesia. While medical treatments can occasionally require
                unforeseen interventions, the initial proposals are designed to
                be as comprehensive and transparent as possible, eliminating
                hidden administrative fees.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-7" data-id="element-687">
              <AccordionTrigger className="text-left text-lg font-semibold" data-id="element-688">
                How is the privacy of my medical data protected?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pt-2 pb-6" data-id="element-689">
                We treat your sensitive health information with the highest
                level of security. All medical records and diagnostic imaging
                are transmitted using secure, encrypted channels. We adhere to
                strict data privacy protocols designed to align with
                international standards for health information portability and
                accountability. Your data is shared only with the specific
                medical teams evaluating your case.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <div className="mt-16 text-center" data-id="element-690">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-691">
            Still have questions?
          </h2>
          <p className="text-slate-600 mb-8" data-id="element-692">
            Our care coordinators are available to provide detailed,
            personalized information.
          </p>
          <Button size="lg" onClick={() => navigate('/contact')} data-id="element-693">
            Contact a Care Coordinator
          </Button>
        </div>
      </main>

      <AppFooter data-id="element-694" />
    </div>;
}